// eslint-disable-next-line import/no-duplicates
import { format, isValid, parseISO } from "date-fns";
// eslint-disable-next-line import/no-duplicates
import { enAU, enCA, enGB, enUS, frCA } from "date-fns/locale";
import i18n from "i18next";

const dateLocales: Record<string, Locale> = {
  en: enUS,
  "en-US": enUS,
  "en-CA": enCA,
  "en-GB": enGB,
  "en-AU": enAU,
  fr: frCA,
  "fr-CA": frCA,
};

export const parseDate = (
  value?: string | Date | null,
  formatString = "P",
  defaultValue = null,
  language = i18n.language
): Date | null => {
  if (!value) {
    return defaultValue;
  }

  if (typeof value === "string") {
    try {
      value = parseISO(value);
    } catch (e) {
      console.warn(`formatDate was called with invalid value ${value}`, e);
      return defaultValue;
    }
  }

  if (!isValid(value)) {
    return defaultValue;
  }

  return value;
};

export const formatDate = (
  value?: string | Date | null,
  formatString = "P",
  defaultValue = "-",
  language = i18n.language
): string => {
  value = parseDate(value);

  if (!value) {
    return defaultValue;
  }

  return format(value, formatString, { locale: dateLocales[language] || enUS });
};

export const formatDateTime = (
  value?: string | Date | null,
  formatString = "Pp",
  defaultValue = "-",
  language = i18n.language
) => {
  return formatDate(value, formatString, defaultValue, language);
};

export const formatTime = (
  value?: string | Date | null,
  formatString = "p",
  defaultValue = "-",
  language = i18n.language
) => {
  return formatDate(value, formatString, defaultValue, language);
};

export const getCurrentTimezoneOffset = () => {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset#negative_values_and_positive_values
  const offset = Math.floor(new Date().getTimezoneOffset() / -60);
  return `${offset < 0 ? "-" : "+"}${String(Math.abs(offset)).padStart(2, "0")}`;
};

/**
 * Converts given offset number in minutes into a timezone name
 */
export function getTimezoneByOffset(offsetMinutes: number) {
  // get all supported timezones sorted by the most popular ones
  const timezones = Intl.supportedValuesOf("timeZone").sort((_, tz) =>
    [
      "America/Vancouver",
      "America/Toronto",
      "America/Edmonton",
      "America/Montreal",
      "Europe/London",
      "Europe/Kiev",
    ].includes(tz)
      ? 1
      : -1
  );

  const staticDate = new Date("2020-01-01T12:00:00Z");
  const staticDateWithOffsetString = new Date(
    staticDate.getTime() + offsetMinutes * 60 * 1000
  ).toLocaleString("en-US", { timeZone: "UTC" });

  return (
    timezones.find((tz) => {
      const timezoneDateString = staticDate.toLocaleString("en-US", {
        timeZone: tz,
      });
      if (staticDateWithOffsetString === timezoneDateString) {
        return true;
      }
      return false;
    }) ?? ""
  );
}
